import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { setAuthState } from '../composables/auth.js';
import { trackQueueEvent } from '../composables/autoapply-queue-analytics.js';
import { isAutoApplyQueueEnabled } from '../composables/autoapply-queue.js';
import { persistTutorialFlag } from '../composables/tutorial-flag.js';
import { useTutorialTour } from '../composables/use-tutorial-tour.js';
import { AUTOAPPLY_QUEUE_EVENTS } from '../constants/analytics-events.js';

// users.options flag recording that the AutoApply Queue tour for existing users (EN-1250) is done with.
export const QUEUE_TUTORIAL_FLAG = 'auto_apply_queue_existing_user_tutorial_passed';

const QUEUE_ROUTE_NAME = 'job-board-autoapply';
// How the tutorial events name this tour, against the new users' (EN-1251).
const TUTORIAL = 'existing_user';
export const AUTOAPPLY_TAB_TARGET = '[data-tutorial-target="autoapply-tab"]';

/**
 * The four steps (Figma 02b–03d), each tied to the page it shows on. The first shows where the seeker clicked
 * AutoApply, Recommended or Saved; the others on the queue, the second around the application just added.
 *
 * @returns {Array<{ routeName: string, target: string, body: string, nextLabel: string }>}
 */
const buildSteps = (originRouteName, applicationUuid) => [
	{
		routeName: originRouteName,
		target: AUTOAPPLY_TAB_TARGET,
		body: 'Your application is in the AutoApply queue. We prepare it in the background, so you can keep browsing jobs.',
		nextLabel: 'Next',
	},
	{
		routeName: QUEUE_ROUTE_NAME,
		target: `[data-application-uuid="${applicationUuid}"]`,
		body: 'Every application you send to AutoApply lives here. Right now we’re tailoring your resume, writing a cover '
			+ 'letter and answering the questions.',
		nextLabel: 'Next',
	},
	{
		routeName: QUEUE_ROUTE_NAME,
		target: '[data-tutorial-target="queue-filters"]',
		body: 'When it’s ready, it moves to Ready to review. If the company asks something only you can answer, it waits '
			+ 'in Needs your input.',
		nextLabel: 'Next',
	},
	{
		routeName: QUEUE_ROUTE_NAME,
		target: '[data-tutorial-target="complete-auto-apply"]',
		body: 'Want to skip the review step? Turn on Complete Auto Apply and ready applications are sent on their own.',
		nextLabel: 'Got it',
	},
];

/**
 * The tour that shows a seeker who used AutoApply before the queue where their application went (EN-1250). It
 * spans the Job Board and the AutoApply Queue, so it lives here rather than in a page: each page's JobBoardShell
 * renders it (queue-tutorial.vue) while the tour is on that page's step.
 */
export const useAutoApplyQueueTutorialStore = defineStore('autoapply-queue-tutorial', () => {
	const router = useRouter();
	const { getUser } = setAuthState();
	const tour = useTutorialTour();

	// Where the seeker clicked AutoApply: the first step shows there, and the tour returns there when it ends.
	let origin = null;
	let stopFollowingRoutes = null;
	// Set once this page load has started the tour or settled the flag. The credits refresh after each add replaces
	// the cached user with one read before the flag was saved, so the cached flag alone cannot be trusted.
	let isSettled = false;
	// The step this run last reported as shown. The tour reports a step again whenever its card re-enters, such as
	// after the exit-intent modal.
	let lastShownStep = null;

	const isHighlightingTab = computed(() => tour.active.value && tour.isFirstStep.value);

	const isOnRoute = (routeName) => router.currentRoute.value.name === routeName;

	// A step the tour cannot reach, as a guard sent it elsewhere, ends the tour rather than leave it waiting.
	const showStepRoute = async (routeName) => {
		if (isOnRoute(routeName)) {
			return;
		}

		await router.push(routeName === origin.name ? origin.fullPath : { name: routeName });

		if (tour.active.value && !isOnRoute(tour.currentStep.value.routeName)) {
			tour.exit();
		}
	};

	const followRoutes = () => {
		stopFollowingRoutes = router.afterEach((to, from, failure) => {
			if (failure || !tour.active.value) {
				return;
			}

			// The Job Board keeps the open job in its address, and moves it to the neighbour after the tour started.
			if (to.name === origin.name) {
				origin.fullPath = to.fullPath;
			}

			// Browser Back, or any other navigation the tour did not make, leaves its step behind.
			if (to.name !== tour.currentStep.value.routeName) {
				tour.exit();
			}
		});
	};

	const onClose = () => {
		stopFollowingRoutes?.();
		stopFollowingRoutes = null;
	};

	const settle = () => {
		isSettled = true;
		persistTutorialFlag(QUEUE_TUTORIAL_FLAG);
	};

	/**
	 * Called once an application has been added to the queue and its card has left the list (EN-1236). Starts the
	 * tour for a seeker who used AutoApply before and has not seen it. A queue add creates the seeker's first
	 * ApplicationSubmission, which makes `has_used_auto_apply` true, so a seeker who had never used AutoApply is told
	 * apart at this add: their flag is set, and they never see this tour.
	 *
	 * @param {{ uuid: string }} application The application just added.
	 * @param {boolean} hadUsedAutoApply `has_used_auto_apply` as it was before the add. The cached user cannot say:
	 *     the credits refresh after the add may already have replaced it with one that has used AutoApply.
	 */
	const startAfterAdd = (application, hadUsedAutoApply) => {
		const user = getUser();

		if (!user || isSettled || user.options?.[QUEUE_TUTORIAL_FLAG] || !isAutoApplyQueueEnabled()) {
			return;
		}

		if (!hadUsedAutoApply) {
			settle();

			return;
		}

		// A page without the AutoApply tab has nowhere to start it, and a seeker who opened the queue while the card
		// collapsed has found it already. The tour waits for their next add.
		if (isOnRoute(QUEUE_ROUTE_NAME) || !document.querySelector(AUTOAPPLY_TAB_TARGET)) {
			return;
		}

		const { name, fullPath } = router.currentRoute.value;
		origin = { name, fullPath };
		lastShownStep = null;

		// Recorded as the tour starts, so a reload or a closed tab mid-tour does not bring it back.
		settle();
		followRoutes();
		tour.start(buildSteps(name, application.uuid), { onClose });
	};

	/**
	 * The tour has shown the card of the step at `stepIndex`: report it once, unless it is the step last reported.
	 *
	 * @param {number} stepIndex
	 */
	const stepShown = (stepIndex) => {
		if (stepIndex === lastShownStep) {
			return;
		}

		lastShownStep = stepIndex;
		trackQueueEvent(AUTOAPPLY_QUEUE_EVENTS.TUTORIAL_STEP_SHOWN, { tutorial: TUTORIAL, step: stepIndex + 1 });
	};

	// The seeker chose to close the tour at the step it is on: not when it ends for another reason.
	const trackClosed = (action) => trackQueueEvent(AUTOAPPLY_QUEUE_EVENTS.TUTORIAL_CLOSED, {
		tutorial: TUTORIAL,
		step: tour.stepIndex.value + 1,
		action,
	});

	/**
	 * "Got it" and Escape: end the tour and return to the Job Board page the seeker started from.
	 */
	const finish = () => {
		if (!tour.active.value) {
			return;
		}

		tour.exit();

		if (!isOnRoute(origin.name)) {
			router.push(origin.fullPath);
		}
	};

	/**
	 * "Next", which moves to the next step's page first, and "Got it" on the last step.
	 */
	const next = () => {
		if (!tour.active.value) {
			return;
		}

		if (tour.isLastStep.value) {
			trackClosed('got_it');
			finish();

			return;
		}

		tour.next();
		showStepRoute(tour.currentStep.value.routeName);
	};

	/**
	 * "Back", and "Exit" on the first step, which ends the tour where the seeker is. The application stays queued.
	 */
	const previous = () => {
		if (!tour.active.value) {
			return;
		}

		if (tour.isFirstStep.value) {
			trackClosed('exit');
			tour.exit();

			return;
		}

		tour.previous();
		showStepRoute(tour.currentStep.value.routeName);
	};

	/**
	 * Escape: ends the tour like "Got it", and reports it as Escape.
	 */
	const escape = () => {
		if (!tour.active.value) {
			return;
		}

		trackClosed('escape');
		finish();
	};

	/**
	 * End the tour where the seeker is: its step's target never mounted.
	 */
	const exit = () => tour.exit();

	/**
	 * Whether the tour is running and `routeName` is the page it started from: a move back there is the tour's own,
	 * which the exit-intent guard lets through (EN-1251).
	 */
	const isOriginRoute = (routeName) => tour.active.value && routeName === origin?.name;

	return {
		active: tour.active,
		stepIndex: tour.stepIndex,
		currentStep: tour.currentStep,
		totalSteps: tour.totalSteps,
		isHighlightingTab,

		startAfterAdd,
		next,
		previous,
		finish,
		escape,
		exit,
		stepShown,
		isOriginRoute,
	};
});
